import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Calendar as CalendarIcon, CalendarDays, CheckCircle2, ChevronDown, ChevronUp, LayoutPanelLeft, Maximize2, MoreHorizontal, Pencil, Plus, ShoppingCart, Trash2, ArrowLeft, Info } from 'lucide-react'
import { assignmentsApi } from '@/services/assignments'
import { financeApi } from '@/services/finance'
import { jobsApi } from '@/services/jobs'
import {
  buildLatestAssignmentsByJobWorkerKey,
  isAssignmentAddableToWorkDay,
  isTerminalAssignmentStatus,
  resolveAssignmentWorkerId,
} from '@/lib/assignment-status'
import {
  getJobScheduleBlockedMessage,
  getJobStatusBadgeClassName,
  getJobStatusLabel,
  getWorkdayCreationJobBlockedMessage,
  getWorkdayEntryJobBlockedMessage,
} from '@/lib/job-status'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Calendar, CalendarDayButton } from '@/components/ui/calendar'
import { SearchableSelect } from '@/components/common/searchable-select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { toast } from 'sonner'
import { getErrorMessage } from '@/lib/api'
import { formatDateForLocale, formatTimeForLocale, preferOrgLocalDateTime, useOrganizationDateTimeFormat } from '@/lib/org-date-locale'
import { WorkerLifecycleNav } from '@/components/workers/worker-lifecycle-nav'
import { JobTitleLink, formatJobTitleWithId } from '@/components/jobs/job-title-link'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'
import { getLocationReturnPath, resolveReturnToPath, withReturnTo } from '@/lib/return-navigation'
import { AddInventoryParticularDialog } from '@/components/workdays/add-inventory-particular-dialog'
import { ChecklistCompletionDialog } from '@/components/jobs/checklist-completion-dialog'
import { WorkDayEntryActionsMenu } from '@/components/workdays/work-day-entry-actions-menu'
import {
  getWorkDayEntryStatusBadgeClassName,
  getWorkDayEntryStatusHint,
  getWorkDayEntryStatusLabel,
  isFinalWorkDayEntryStatus,
  resolveWorkDayEntryStatusFromEntry,
  type WorkDayEntryStatus,
} from '@/lib/workday-entry-status'
import { cn, formatCurrency } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth-store'
import type { Job, Particular } from '@/types'

const formatDate = (value?: string | Date | null, locale = 'en-US') => {
  if (!value) {
    return '-'
  }

  const parsed = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return String(value)
  }

  return formatDateForLocale(parsed, locale)
}

const formatWorkDayDateWithWeekday = (value?: string | null, locale = 'en-US') => {
  if (!value?.trim()) {
    return '-'
  }

  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(value.trim())
    ? new Date(`${value.trim()}T12:00:00`)
    : new Date(value)

  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleDateString(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const formatWindowTimeLabel = (
  start?: string | null,
  end?: string | null,
  startLocal?: string | null,
  endLocal?: string | null,
  locale = 'en-US',
  timeZone?: string,
) => {
  const startValue = preferOrgLocalDateTime(start, startLocal)
  const endValue = preferOrgLocalDateTime(end, endLocal)

  const startDate = toValidDate(startValue)
  const endDate = toValidDate(endValue)

  if (!startDate && !endDate) {
    return null
  }

  if (startDate && endDate) {
    return `${formatTimeForLocale(startDate, locale, '-', timeZone)} - ${formatTimeForLocale(endDate, locale, '-', timeZone)}`
  }

  return formatTimeForLocale(startDate || endDate!, locale, '-', timeZone)
}

const toValidDate = (value?: string | null) => {
  if (!value) {
    return null
  }

  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const formatMoney = (value?: string | number | null) => {
  if (value === null || value === undefined || value === '') {
    return '-'
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? formatCurrency(parsed) : String(value)
}

const formatDurationLabel = (start?: string | null, end?: string | null) => {
  const startDate = toValidDate(start)
  const endDate = toValidDate(end)

  if (!startDate || !endDate) {
    return null
  }

  const durationHours = (endDate.getTime() - startDate.getTime()) / 3_600_000
  if (!Number.isFinite(durationHours) || durationHours < 0) {
    return null
  }

  return `${formatHours(durationHours)} hrs`
}

const formatHours = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') {
    return '-'
  }

  const parsed = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(parsed)) {
    return String(value)
  }

  return Number.isInteger(parsed) ? String(parsed) : parsed.toFixed(2).replace(/\.?0+$/, '')
}

const formatMinutes = (value?: number | string | null) => {
  const parsed = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return ''
  }

  const minutes = Math.round(parsed)
  const hours = Math.floor(minutes / 60)
  const remainder = minutes % 60
  if (hours > 0 && remainder > 0) return `${hours}h ${remainder}m`
  if (hours > 0) return `${hours}h`
  return `${remainder}m`
}

const getStatusBadgeClassName = (status?: string) => {
  return getWorkDayEntryStatusBadgeClassName(status)
}

const patchWorkDayEntryStatusInCache = (
  current: unknown,
  entryId: string,
  status: string,
) => {
  if (!current || typeof current !== 'object') {
    return current
  }

  const record = current as Record<string, unknown>
  if (!Array.isArray(record.results)) {
    return current
  }

  const nextStatusLabel = getWorkDayEntryStatusLabel(status)

  return {
    ...record,
    results: record.results.map((item) => {
      if (!item || typeof item !== 'object') {
        return item
      }

      const row = item as Record<string, unknown>
      if (String(row.id ?? '') !== String(entryId)) {
        return item
      }

      return {
        ...row,
        status,
        status_label: nextStatusLabel,
      }
    }),
  }
}

const toIsoDate = (value: Date) => {
  const localDate = new Date(value)
  localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset())
  return localDate.toISOString().slice(0, 10)
}

const getDateKey = (value?: string | null) => {
  if (!value) {
    return null
  }

  const trimmed = value.trim()
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return trimmed.slice(0, 10)
  }

  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }

  return toIsoDate(parsed)
}

const parseDateParam = (value?: string | null) => {
  const dateKey = getDateKey(value)
  if (!dateKey) {
    return new Date()
  }

  return new Date(`${dateKey}T00:00:00`)
}

const formatNumber = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') {
    return '-'
  }

  const parsed = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(parsed)) {
    return String(value)
  }

  return Number.isInteger(parsed) ? String(parsed) : parsed.toFixed(2).replace(/\.?0+$/, '')
}

const truncateText = (value?: string | null, maxLength = 72) => {
  if (!value) {
    return null
  }

  const normalized = value.trim()
  if (!normalized) {
    return null
  }

  if (normalized.length <= maxLength) {
    return normalized
  }

  return `${normalized.slice(0, maxLength - 1)}…`
}

type WorkDayListItem = {
  id: string | number
  work_date: string
  job: string | number
  is_planned?: boolean
  is_worked?: boolean
  notes?: string
  worker_entries_count?: number
  resources_count?: number
}

type WorkerFilterOption = {
  id: string
  label: string
}

type CalendarViewMode = 'workers' | 'resources'

type CalendarDaySummary = {
  entryCount: number
  workerCount: number
  jobCount: number
  hasAttention: boolean
  resourceQuantity?: number
  resourceTitles?: number
}

type ResourceCalendarItem = Particular & {
  dateKey: string
  quantityValue: number
  titleLabel: string
  jobId?: string
  jobLabel: string
}

const WORKDAYS_CALENDAR_MODE_STORAGE_KEY = 'workdays-calendar-mode'
const WORKDAYS_PAGE_LAYOUT_STORAGE_KEY = 'workdays-page-layout'

type WorkdaysPageLayout = 'split' | 'full'

type WorkDayEntryListItem = {
  id: string | number
  work_day: string | number
  worker: string | number
  worker_name?: string
  job: string | number
  job_title?: string
  work_date?: string
  planned_start?: string | null
  planned_start_local?: string | null
  planned_end?: string | null
  planned_end_local?: string | null
  check_in?: string | null
  check_in_local?: string | null
  check_out?: string | null
  check_out_local?: string | null
  hours?: number | string
  planned_duration_minutes?: number
  actual_duration_minutes?: number
  is_late?: boolean
  late_minutes?: number
  has_overtime?: boolean
  overtime_minutes?: number
  payable_by_client?: boolean
  client_pay_amount?: number | string | null
  total_pay?: number | string
  needs_supervisor_attention?: boolean
  status?: string
  status_label?: string
  status_is_final?: boolean
  status_hint?: string
  notes?: string
}

const WORKDAY_DELETE_ALLOWED_ENTRY_STATUSES = new Set<WorkDayEntryStatus>([
  'draft',
  'pending_acceptance',
  'scheduled',
  'schedule_declined',
])

const canEntryBeDeletedWithWorkday = (entry: WorkDayEntryListItem) => {
  const status = resolveWorkDayEntryStatusFromEntry(entry)
  const reviewStatus = String((entry as WorkDayEntryListItem & { review_status?: string | null }).review_status ?? '').trim()

  return Boolean(status && WORKDAY_DELETE_ALLOWED_ENTRY_STATUSES.has(status))
    && !entry.check_in
    && !entry.check_out
    && !reviewStatus
}

type WorkDayCalendarButtonProps = React.ComponentProps<typeof CalendarDayButton> & {
  calendarMode: CalendarViewMode
  calendarSummaryByDate: Map<string, CalendarDaySummary>
  showWorkerAndJobCounts: boolean
  attentionDates: Set<string>
  workDayByDate: Map<string, WorkDayListItem>
}

function WorkDayCalendarButton({
  className,
  day,
  calendarMode,
  calendarSummaryByDate,
  showWorkerAndJobCounts,
  attentionDates,
  workDayByDate,
  ...props
}: WorkDayCalendarButtonProps) {
  const isoDate = toIsoDate(day.date)
  const summary = calendarSummaryByDate.get(isoDate)
  const workDay = calendarMode === 'workers' ? workDayByDate.get(isoDate) : undefined
  const hasAttention = calendarMode === 'workers' ? attentionDates.has(isoDate) : false
  const hasResources = calendarMode === 'resources' && Boolean(summary)

  return (
    <CalendarDayButton
      {...props}
      day={day}
      className={cn(
        className,
        'relative gap-0.5 overflow-hidden px-0.5 py-1',
        hasResources
          ? 'border border-sky-200 bg-sky-50/70 text-sky-950 hover:bg-sky-100/80'
          : workDay
          ? 'border border-emerald-200 bg-emerald-50/50 text-emerald-950 hover:bg-emerald-100/70'
          : undefined
      )}
    >
      {hasAttention ? (
        <span
          className="absolute right-0.5 top-0.5 z-10 flex h-3.5 w-3.5 items-center justify-center rounded-full border border-amber-400 bg-amber-500 text-[9px] font-bold leading-none text-white shadow-sm"
          title="Needs review"
          aria-label="Needs review"
        >
          !
        </span>
      ) : null}
      <span className="text-xs font-medium leading-none">{day.date.getDate()}</span>
      {summary ? (
        calendarMode === 'resources' ? (
          <span className="rounded-full border border-slate-900/10 bg-slate-900 px-1 py-0 text-[8px] font-semibold leading-none text-white shadow-sm opacity-100">
            R{formatNumber(summary.resourceQuantity ?? summary.entryCount)}
          </span>
        ) : showWorkerAndJobCounts ? (
          <span className="flex max-w-full items-center justify-center gap-0.5 text-[8px] font-semibold leading-none opacity-100">
            <span className="rounded-full border border-slate-900/10 bg-slate-900 px-1 py-0 text-white shadow-sm">W{summary.workerCount}</span>
            <span className="rounded-full border border-emerald-950/10 bg-emerald-700 px-1 py-0 text-white shadow-sm">J{summary.jobCount}</span>
          </span>
        ) : (
          <span className="rounded-full border border-sky-950/10 bg-sky-700 px-1 py-0 text-[8px] font-semibold leading-none text-white shadow-sm opacity-100">
            {summary.entryCount}
          </span>
        )
      ) : workDay ? (
        <span className="text-[8px] font-semibold leading-none text-emerald-700 opacity-100">WD</span>
      ) : null}
    </CalendarDayButton>
  )
}

type WorkDayEntryCardProps = {
  entry: WorkDayEntryListItem
  showJob: boolean
  serviceInstanceId: string | null
  returnPath?: string | null
  organizationLocale: string
  organizationTimeZone?: string
  blocked?: boolean
  assignmentBlocked?: boolean
  assignmentStatus?: string | null
  isDeleting?: boolean
  onDelete?: () => void
  onStatusChange?: (
    status: WorkDayEntryStatus,
    options?: { checkIn?: string; checkOut?: string; reason?: string },
  ) => void
  allowGroupWorkdayAccess?: boolean
}

function WorkDayEntryCard({
  entry,
  showJob,
  serviceInstanceId,
  returnPath,
  organizationLocale,
  organizationTimeZone,
  blocked = false,
  assignmentBlocked = false,
  assignmentStatus,
  isDeleting = false,
  onDelete,
  onStatusChange,
  allowGroupWorkdayAccess = false,
}: WorkDayEntryCardProps) {
  const labels = useWorkspaceLabels()
  const plannedTime = formatWindowTimeLabel(
    entry.planned_start,
    entry.planned_end,
    entry.planned_start_local,
    entry.planned_end_local,
    organizationLocale,
    organizationTimeZone,
  )
  const actualTime = formatWindowTimeLabel(
    entry.check_in,
    entry.check_out,
    entry.check_in_local,
    entry.check_out_local,
    organizationLocale,
    organizationTimeZone,
  )
  const plannedDuration = formatDurationLabel(entry.planned_start, entry.planned_end)
  const entryStatus = resolveWorkDayEntryStatusFromEntry(entry)
  const statusLabel = entry.status_label || getWorkDayEntryStatusLabel(entryStatus)
  const statusHint = entry.status_hint || getWorkDayEntryStatusHint(entryStatus)
  const statusIsFinal = entry.status_is_final ?? isFinalWorkDayEntryStatus(entryStatus)

  return (
    <div className="rounded-lg border border-border/60 bg-card/80 px-3 py-2">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={withServiceInstanceId(`/workers/${entry.worker}`, serviceInstanceId)}
              className="truncate text-sm font-semibold text-foreground hover:text-primary hover:underline"
            >
              {entry.worker_name || `Worker #${entry.worker}`}
            </Link>
            <Badge variant="outline" className={cn('h-5 px-1.5 text-[10px]', getStatusBadgeClassName(entryStatus ?? entry.status))}>
              {statusLabel}
            </Badge>
            {statusIsFinal ? (
              <Badge variant="outline" className="h-5 border-teal-300 bg-teal-50 px-1.5 text-[10px] text-teal-800">
                Final
              </Badge>
            ) : null}
            {entry.is_late ? (
              <Badge
                variant="outline"
                className="h-5 border-rose-200 bg-rose-50 px-1.5 text-[10px] text-rose-700"
                title="Check-in was more than 5 minutes after planned start"
              >
                Late{entry.late_minutes ? ` ${formatMinutes(entry.late_minutes)}` : ''}
              </Badge>
            ) : null}
            {entry.has_overtime ? (
              <Badge
                variant="outline"
                className="h-5 border-violet-200 bg-violet-50 px-1.5 text-[10px] text-violet-700"
                title="Actual work duration is greater than planned duration"
              >
                Overtime{entry.overtime_minutes ? ` ${formatMinutes(entry.overtime_minutes)}` : ''}
              </Badge>
            ) : null}
            {entry.needs_supervisor_attention ? (
              <Badge
                variant="outline"
                className="h-5 min-w-5 justify-center border-amber-300 bg-amber-500 px-1 text-[10px] font-bold text-white"
                title="Needs review"
              >
                !
              </Badge>
            ) : null}
          </div>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {plannedTime ? `Planned ${plannedTime}` : 'Not planned'}
            {plannedDuration ? ` · ${plannedDuration}` : ''}
            {actualTime ? ` · Actual ${actualTime}` : ''}
            {showJob && entry.job ? (
              <>
                {' · '}
                <JobTitleLink
                  jobId={entry.job}
                  title={entry.job_title}
                  entityLabel={labels.entity}
                  detailBasePath={labels.basePath}
                  serviceInstanceId={serviceInstanceId}
                  returnPath={returnPath}
                  className="font-medium"
                />
              </>
            ) : null}
            {statusHint ? ` · ${statusHint}` : ''}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <div className="hidden items-center gap-3 text-xs text-muted-foreground sm:flex">
            <span className="whitespace-nowrap">{formatHours(entry.hours)} hrs</span>
            <span className="whitespace-nowrap font-medium text-foreground">{formatMoney(entry.total_pay)}</span>
          </div>
          <WorkDayEntryActionsMenu
            entry={{ ...entry, status: entryStatus ?? entry.status }}
            serviceInstanceId={serviceInstanceId}
            blocked={blocked}
            assignmentBlocked={assignmentBlocked}
            assignmentStatus={assignmentStatus}
            showDelete
            alwaysShowTrigger
            isDeleting={isDeleting}
            onDelete={onDelete}
            onStatusChange={onStatusChange}
            allowGroupWorkdayAccess={allowGroupWorkdayAccess}
          />
        </div>
      </div>
    </div>
  )
}

type WorkDaysListPageProps = {
  portalMode?: 'job' | 'project' | 'event'
}

export default function WorkDaysListPage({ portalMode: _portalMode = 'job' }: WorkDaysListPageProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const labels = useWorkspaceLabels()
  const isEventMode = labels.mode === 'event'
  const isProjectMode = labels.mode === 'project'
  const workerEntitySingular = isEventMode ? 'Crew member' : 'Worker'
  const workerEntityPlural = isEventMode ? 'Crew' : 'Workers'
  const workerEntitySingularLower = workerEntitySingular.toLowerCase()
  const workerEntityPluralLower = workerEntityPlural.toLowerCase()
  const canGroupWorkdayStatusOverride = useAuthStore(
    (state) => Boolean(state.currentPost?.capabilities?.can_access_group_workday_access),
  )
  const currentReturnPath = getLocationReturnPath(location)
  const { locale: organizationDateLocale, timeZone: organizationTimeZone } =
    useOrganizationDateTimeFormat(serviceInstanceId)
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const requestedJobId = searchParams.get('job')?.trim() || ''
  const isJobFilterLocked = Boolean(requestedJobId)
  const requestedWorkerId =
    searchParams.get('worker')?.trim()
    || searchParams.get('worker_id')?.trim()
    || ''
  const requestedCalendarMode = searchParams.get('view') === 'resources'
    ? 'resources'
    : searchParams.has('view')
      ? 'workers'
      : null
  const requestedDate = searchParams.get('date')?.trim() || ''
  const requestedSelectedDate = useMemo(() => parseDateParam(requestedDate), [requestedDate])
  const [selectedJobId, setSelectedJobId] = useState(requestedJobId || 'all')
  const [selectedWorkerId, setSelectedWorkerId] = useState(requestedWorkerId || 'all')
  const [calendarMode, setCalendarMode] = useState<CalendarViewMode>(() => {
    if (requestedCalendarMode) {
      return requestedCalendarMode
    }

    if (typeof window === 'undefined') {
      return 'workers'
    }

    const storedMode = window.localStorage.getItem(WORKDAYS_CALENDAR_MODE_STORAGE_KEY)
    return storedMode === 'resources' ? 'resources' : 'workers'
  })
  const [pageLayout, setPageLayout] = useState<WorkdaysPageLayout>(() => {
    if (typeof window === 'undefined') {
      return 'split'
    }

    return window.localStorage.getItem(WORKDAYS_PAGE_LAYOUT_STORAGE_KEY) === 'full' ? 'full' : 'split'
  })
  const isFullCalendarView = pageLayout === 'full'
  const [showAttentionOnly, setShowAttentionOnly] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Date>(requestedSelectedDate)
  const [visibleMonth, setVisibleMonth] = useState<Date>(requestedSelectedDate)
  const [selectedWorkDayNotes, setSelectedWorkDayNotes] = useState('')
  const [selectedWorkDayPlanned, setSelectedWorkDayPlanned] = useState(false)
  const [selectedWorkDayWorked, setSelectedWorkDayWorked] = useState(false)
  const [isDeleteWorkDayOpen, setIsDeleteWorkDayOpen] = useState(false)
  const [showWorkDayDetails, setShowWorkDayDetails] = useState(false)
  const [editingParticular, setEditingParticular] = useState<Particular | null>(null)
  const [isEditParticularOpen, setIsEditParticularOpen] = useState(false)
  const [pendingEntryCompletion, setPendingEntryCompletion] = useState<{
    id: string
    status: string
    checkIn?: string
    checkOut?: string
    reason?: string
    jobId: string
    workDayId: string | number
  } | null>(null)
  const [jobSearch, setJobSearch] = useState('')
  const [debouncedJobSearch, setDebouncedJobSearch] = useState('')

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedJobSearch(jobSearch.trim())
    }, 300)

    return () => window.clearTimeout(timeoutId)
  }, [jobSearch])

  const jobSearchQuery = useQuery({
    queryKey: ['jobs', 'workdays-search', debouncedJobSearch, labels.mode],
    queryFn: () =>
      isEventMode
        ? jobsApi.getEvents({ search: debouncedJobSearch || undefined, page: 1, page_size: 50 })
      : isProjectMode
        ? jobsApi.getProjects({
            page: 1,
            page_size: 25,
            search: debouncedJobSearch,
            ordering: '-updated_at',
          })
        : jobsApi.getJobs({
            page: 1,
            page_size: 25,
            search: debouncedJobSearch,
            ordering: '-updated_at',
          }),
    enabled: debouncedJobSearch.length >= 2,
    staleTime: 30_000,
  })
  const selectedJobQuery = useQuery({
    queryKey: ['job', selectedJobId, serviceInstanceId],
    queryFn: () => jobsApi.getJob(
      selectedJobId,
      serviceInstanceId ? { service_instance_id: serviceInstanceId } : undefined,
    ),
    enabled: selectedJobId !== 'all' && Boolean(selectedJobId),
    staleTime: 30_000,
  })

  useEffect(() => {
    setSelectedJobId(requestedJobId || 'all')
  }, [requestedJobId])

  useEffect(() => {
    setSelectedWorkerId(requestedWorkerId || 'all')
  }, [requestedWorkerId])

  useEffect(() => {
    setSelectedDate(requestedSelectedDate)
    setVisibleMonth(requestedSelectedDate)
  }, [requestedSelectedDate])

  useEffect(() => {
    window.localStorage.setItem(WORKDAYS_CALENDAR_MODE_STORAGE_KEY, calendarMode)
  }, [calendarMode])

  useEffect(() => {
    window.localStorage.setItem(WORKDAYS_PAGE_LAYOUT_STORAGE_KEY, pageLayout)
  }, [pageLayout])

  const handleJobChange = (value: string) => {
    if (isJobFilterLocked) {
      return
    }

    setSelectedJobId(value)

    const nextSearchParams = new URLSearchParams(location.search)
    if (value === 'all') {
      nextSearchParams.delete('job')
    } else {
      nextSearchParams.set('job', value)
    }

    const nextSearch = nextSearchParams.toString()
    const currentSearch = location.search.startsWith('?') ? location.search.slice(1) : location.search
    if (nextSearch !== currentSearch) {
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : '',
        },
        { replace: true },
      )
    }
  }

  useEffect(() => {
    const nextSearchParams = new URLSearchParams(location.search)

    if (calendarMode === 'resources') {
      nextSearchParams.set('view', 'resources')
    } else {
      nextSearchParams.delete('view')
    }

    const nextSearch = nextSearchParams.toString()
    const currentSearch = location.search.startsWith('?') ? location.search.slice(1) : location.search

    if (nextSearch !== currentSearch) {
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : '',
        },
        { replace: true }
      )
    }
  }, [calendarMode, location.pathname, location.search, navigate])

  useEffect(() => {
    if (requestedJobId && selectedJobId === requestedJobId && requestedWorkerId) {
      setSelectedWorkerId(requestedWorkerId)
      return
    }

    setSelectedWorkerId('all')
    setShowAttentionOnly(false)
  }, [requestedJobId, requestedWorkerId, selectedJobId])

  useEffect(() => {
    if (calendarMode === 'resources') {
      setShowAttentionOnly(false)
    }
  }, [calendarMode])

  const workDaysQuery = useQuery({
    queryKey: ['workdays', 'calendar', selectedJobId],
    queryFn: () => assignmentsApi.getWorkDays({ job_id: selectedJobId === 'all' ? undefined : selectedJobId, page: 1, page_size: 500 }),
    staleTime: 30_000,
  })

  const selectedDateIso = toIsoDate(selectedDate)
  const isAllJobsSelected = selectedJobId === 'all'
  const calendarWorkDays = (workDaysQuery.data?.results ?? []) as WorkDayListItem[]
  const selectedWorkDayForQuery = isAllJobsSelected
    ? undefined
    : calendarWorkDays.find((workDay) => workDay.work_date === selectedDateIso)

  const selectedDateEntriesQuery = useQuery({
    queryKey: [
      'workday-entries',
      'selected-date',
      selectedJobId,
      selectedDateIso,
      isAllJobsSelected ? 'all-jobs' : String(selectedWorkDayForQuery?.id ?? ''),
      selectedWorkerId,
      showAttentionOnly,
    ],
    queryFn: () =>
      assignmentsApi.getWorkDayEntries({
        job_id: selectedJobId === 'all' ? undefined : selectedJobId,
        work_day_id: !isAllJobsSelected && selectedWorkDayForQuery ? String(selectedWorkDayForQuery.id) : undefined,
        work_date: isAllJobsSelected ? selectedDateIso : undefined,
        worker_id: selectedWorkerId === 'all' ? undefined : selectedWorkerId,
        needs_supervisor_attention: showAttentionOnly ? true : undefined,
        page: 1,
        page_size: 200,
      }),
    enabled: calendarMode === 'workers' && (isAllJobsSelected || Boolean(selectedWorkDayForQuery)),
    staleTime: 15_000,
  })

  const attentionEntriesQuery = useQuery({
    queryKey: ['workday-entries', 'attention', selectedJobId],
    queryFn: () =>
      assignmentsApi.getWorkDayEntries({
        job_id: selectedJobId === 'all' ? undefined : selectedJobId,
        needs_supervisor_attention: true,
        page: 1,
        page_size: 200,
      }),
    enabled: calendarMode === 'workers',
    staleTime: 30_000,
  })

  const assignmentsQuery = useQuery({
    queryKey: ['assignments', 'workdays-calendar', selectedJobId],
    queryFn: () => assignmentsApi.getAssignments({ job_id: selectedJobId === 'all' ? undefined : selectedJobId, page: 1, page_size: 500 }),
    enabled:
      calendarMode === 'workers'
      && (
        Boolean(selectedWorkDayForQuery)
        || (
          isAllJobsSelected
          && selectedDateEntriesQuery.isFetched
          && (selectedDateEntriesQuery.data?.results?.length ?? 0) > 0
        )
      ),
    staleTime: 30_000,
  })

  const particularsQuery = useQuery({
    queryKey: ['particulars', 'workdays-calendar', selectedJobId],
    queryFn: () =>
      financeApi.getParticulars({
        job_id: selectedJobId === 'all' ? undefined : selectedJobId,
        page: 1,
        page_size: 500,
      }),
    staleTime: 30_000,
  })

  const workDays = calendarWorkDays
  const selectedJob = selectedJobId === 'all' ? undefined : selectedJobQuery.data
  const selectedDateEntries = (selectedDateEntriesQuery.data?.results ?? []) as WorkDayEntryListItem[]

  const entryJobIds = useMemo(() => {
    if (!isAllJobsSelected) {
      return []
    }

    return [...new Set(selectedDateEntries.map((entry) => String(entry.job)).filter(Boolean))]
  }, [isAllJobsSelected, selectedDateEntries])

  const entryJobsQueries = useQueries({
    queries: entryJobIds.map((jobId) => ({
      queryKey: ['job', jobId, serviceInstanceId],
      queryFn: () =>
        jobsApi.getJob(
          jobId,
          serviceInstanceId ? { service_instance_id: serviceInstanceId } : undefined,
        ),
      enabled: Boolean(jobId),
      staleTime: 60_000,
    })),
  })

  const jobOptions = useMemo(() => {
    const options: Array<{ value: string; label: string; keywords?: string }> = [
      { value: 'all', label: 'All jobs', keywords: 'all jobs' },
    ]
    const seen = new Set<string>(['all'])

    const addJob = (job: Job) => {
      const jobId = String(job.id)
      if (!jobId || seen.has(jobId)) {
        return
      }

      seen.add(jobId)
      options.push({
        value: jobId,
        label: formatJobTitleWithId({ jobId: job.id, title: job.title, jobRef: job.job_ref }),
        keywords: `${job.title || ''} ${job.description || ''} ${job.job_ref || ''}`.trim(),
      })
    }

    if (selectedJob) {
      addJob(selectedJob)
    }

    for (const job of jobSearchQuery.data?.results ?? []) {
      addJob(job)
    }

    return options
  }, [jobSearchQuery.data?.results, selectedJob])

  const jobTitleById = useMemo(() => {
    const titles = new Map<string, string>()

    const addJob = (job?: Job | null) => {
      if (!job?.id) {
        return
      }

      titles.set(
        String(job.id),
        formatJobTitleWithId({ jobId: job.id, title: job.title, jobRef: job.job_ref }),
      )
    }

    addJob(selectedJob)
    for (const job of jobSearchQuery.data?.results ?? []) {
      addJob(job)
    }
    for (const query of entryJobsQueries) {
      addJob(query.data)
    }

    return titles
  }, [entryJobsQueries, jobSearchQuery.data?.results, selectedJob])

  const jobById = useMemo(() => {
    const jobs = new Map<string, Job>()

    const addJob = (job?: Job | null) => {
      if (!job?.id) {
        return
      }

      jobs.set(String(job.id), job)
    }

    addJob(selectedJob)
    for (const job of jobSearchQuery.data?.results ?? []) {
      addJob(job)
    }
    for (const query of entryJobsQueries) {
      addJob(query.data)
    }

    return jobs
  }, [entryJobsQueries, jobSearchQuery.data?.results, selectedJob])

  const workerOptions = useMemo<WorkerFilterOption[]>(() => {
    const seen = new Set<string>()

    const addWorker = (workerId: string, label: string) => {
      if (!workerId || seen.has(workerId)) {
        return
      }

      seen.add(workerId)
      return { id: workerId, label }
    }

    const options = (assignmentsQuery.data?.results ?? []).reduce<WorkerFilterOption[]>((accumulator, assignment) => {
      const workerId = String(assignment.worker_id ?? '')
      const option = addWorker(workerId, assignment.worker_name || `${workerEntitySingular} #${workerId}`)
      if (option) {
        accumulator.push(option)
      }
      return accumulator
    }, [])

    for (const entry of selectedDateEntries) {
      const workerId = String(entry.worker ?? '')
      const option = addWorker(workerId, entry.worker_name || `${workerEntitySingular} #${workerId}`)
      if (option && !options.some((item) => item.id === option.id)) {
        options.push(option)
      }
    }

    return options.sort((left, right) => left.label.localeCompare(right.label))
  }, [assignmentsQuery.data?.results, selectedDateEntries])

  const workerFilterOptions = useMemo(
    () => [
      {
        value: 'all',
        label: `All ${workerEntityPluralLower}`,
        keywords: `all ${workerEntityPluralLower}`,
      },
      ...workerOptions.map((worker) => ({
        value: worker.id,
        label: worker.label,
        keywords: worker.id,
      })),
    ],
    [workerEntityPluralLower, workerOptions],
  )

  const assignmentByWorkerKey = useMemo(
    () => buildLatestAssignmentsByJobWorkerKey(assignmentsQuery.data?.results ?? []),
    [assignmentsQuery.data?.results],
  )

  const selectedWorkerLabel = useMemo(() => {
    if (selectedWorkerId === 'all') {
      return `All ${workerEntityPluralLower}`
    }

    return workerOptions.find((option) => option.id === selectedWorkerId)?.label || `${workerEntitySingular} #${selectedWorkerId}`
  }, [selectedWorkerId, workerEntityPluralLower, workerEntitySingular, workerOptions])

  const resourceItems = useMemo<ResourceCalendarItem[]>(() => {
    return ((particularsQuery.data?.results ?? []) as Particular[]).reduce<ResourceCalendarItem[]>((accumulator, particular) => {
      const dateKey = getDateKey(particular.purchase_by)
      if (!dateKey) {
        return accumulator
      }

      const rawJobId = particular.job_id ?? (typeof particular.job === 'object' ? particular.job?.id : particular.job)
      const parsedQuantity = Number(particular.quantity)
      accumulator.push({
        ...particular,
        dateKey,
        quantityValue: Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1,
        titleLabel: particular.title?.trim() || 'Untitled particular',
        jobId: rawJobId ? String(rawJobId) : undefined,
        jobLabel: rawJobId ? jobTitleById.get(String(rawJobId)) || `${labels.entity} #${rawJobId}` : labels.unknownEntity,
      })
      return accumulator
    }, [])
  }, [jobTitleById, labels.entity, labels.unknownEntity, particularsQuery.data?.results])

  const workDayMap = useMemo(() => {
    return new Map(workDays.map((workDay) => [workDay.work_date, workDay]))
  }, [workDays])

  const selectedWorkDay = isAllJobsSelected ? undefined : workDayMap.get(selectedDateIso)

  const jobScheduleBlockedMessage = useMemo(() => {
    if (isAllJobsSelected || !selectedJob) {
      return null
    }

    return getJobScheduleBlockedMessage(selectedJob)
  }, [isAllJobsSelected, selectedJob])

  const createBlockedReason = useMemo(() => {
    if (isAllJobsSelected) {
      return `Choose a specific ${labels.entityLower} to create or manage ${labels.workDayPluralLower}.`
    }

    const workdayCreationBlocked = getWorkdayCreationJobBlockedMessage(selectedJob)
    if (workdayCreationBlocked) {
      return workdayCreationBlocked
    }

    if (selectedWorkDay) {
      return `A ${labels.workDayLower} already exists for this ${labels.entityLower} on the selected date.`
    }

    return null
  }, [isAllJobsSelected, labels.entityLower, labels.workDayLower, labels.workDayPluralLower, selectedJob, selectedWorkDay])

  const addableWorkerAssignments = useMemo(() => {
    if (!selectedWorkDay || isAllJobsSelected) {
      return []
    }

    const seenWorkers = new Set<string>()

    return [...(assignmentsQuery.data?.results ?? [])]
      .filter((assignment) => {
        if (!isAssignmentAddableToWorkDay(assignment, selectedDateEntries)) {
          return false
        }

        const workerId = resolveAssignmentWorkerId(assignment)
        if (!workerId || seenWorkers.has(workerId)) {
          return false
        }

        seenWorkers.add(workerId)
        return true
      })
      .sort((left, right) => String(left.worker_name ?? '').localeCompare(String(right.worker_name ?? '')))
  }, [assignmentsQuery.data?.results, isAllJobsSelected, selectedDateEntries, selectedWorkDay])

  const workdayEntryCreationBlockedMessage = useMemo(
    () => getWorkdayEntryJobBlockedMessage(selectedJob),
    [selectedJob],
  )

  const manualWorkDayEntryPath = useMemo(() => {
    if (!selectedWorkDay || isAllJobsSelected || !selectedJobId || workdayEntryCreationBlockedMessage) {
      return null
    }

    const params = new URLSearchParams()
    params.set('job', selectedJobId)
    params.set('work_day', String(selectedWorkDay.id))
    if (selectedWorkDay.work_date) {
      params.set('work_date', selectedWorkDay.work_date)
    }

    return withServiceInstanceId(
      withReturnTo(`/workdays/create?${params.toString()}`, currentReturnPath),
      serviceInstanceId,
    )
  }, [
    currentReturnPath,
    isAllJobsSelected,
    selectedJobId,
    selectedWorkDay,
    serviceInstanceId,
    workdayEntryCreationBlockedMessage,
  ])

  const isWorkDayMutationDisabled = Boolean(jobScheduleBlockedMessage)

  const canDeleteSelectedWorkDay = selectedDateEntries.every(canEntryBeDeletedWithWorkday)

  const showTopWorkerFilter = calendarMode === 'workers' && (isAllJobsSelected || Boolean(selectedWorkDay))

  const selectedDateResources = useMemo(() => {
    return resourceItems.filter((item) => item.dateKey === selectedDateIso)
  }, [resourceItems, selectedDateIso])

  const visibleMonthWorkDays = useMemo(() => {
    return workDays.filter((workDay) => {
      const date = new Date(workDay.work_date)
      return date.getFullYear() === visibleMonth.getFullYear() && date.getMonth() === visibleMonth.getMonth()
    })
  }, [visibleMonth, workDays])

  const attentionDates = useMemo(() => {
    return new Set(
      (attentionEntriesQuery.data?.results ?? [])
        .map((entry) => String((entry as WorkDayEntryListItem).work_date ?? '').trim())
        .filter(Boolean),
    )
  }, [attentionEntriesQuery.data?.results])

  const attentionNeededJobsCount = useMemo(() => {
    const month = visibleMonth.getMonth()
    const year = visibleMonth.getFullYear()

    return new Set(
      (attentionEntriesQuery.data?.results ?? [])
        .filter((entry) => {
          const workDate = String((entry as WorkDayEntryListItem).work_date ?? '').trim()
          if (!workDate) {
            return false
          }

          const parsed = new Date(`${workDate}T12:00:00`)
          return parsed.getFullYear() === year && parsed.getMonth() === month
        })
        .map((entry) => String((entry as WorkDayEntryListItem).job)),
    ).size
  }, [attentionEntriesQuery.data?.results, visibleMonth])

  const calendarSummaryByDate = useMemo(() => {
    const summaries = new Map<string, {
      entryCount: number
      workerCount: number
      jobCount: number
      hasAttention: boolean
      jobIds: Set<string>
    }>()

    workDays.forEach((workDay) => {
      if (!workDay.work_date) {
        return
      }

      const existing = summaries.get(workDay.work_date) ?? {
        entryCount: 0,
        workerCount: 0,
        jobCount: 0,
        hasAttention: false,
        jobIds: new Set<string>(),
      }
      const entryCount = Number(workDay.worker_entries_count ?? 0)
      existing.entryCount += entryCount
      existing.workerCount += entryCount
      existing.jobIds.add(String(workDay.job))
      existing.hasAttention = existing.hasAttention || attentionDates.has(workDay.work_date)
      summaries.set(workDay.work_date, existing)
    })

    return new Map(
      Array.from(summaries.entries()).map(([date, summary]) => [
        date,
        {
          entryCount: summary.entryCount,
          workerCount: summary.workerCount,
          jobCount: summary.jobIds.size,
          hasAttention: summary.hasAttention,
        },
      ]),
    )
  }, [attentionDates, workDays])

  const resourceSummaryByDate = useMemo(() => {
    const summaries = new Map<string, { recordCount: number; quantityTotal: number; titleLabels: Set<string>; jobLabels: Set<string> }>()

    resourceItems.forEach((item) => {
      const existing = summaries.get(item.dateKey) ?? {
        recordCount: 0,
        quantityTotal: 0,
        titleLabels: new Set<string>(),
        jobLabels: new Set<string>(),
      }
      existing.recordCount += 1
      existing.quantityTotal += item.quantityValue
      existing.titleLabels.add(item.titleLabel)
      existing.jobLabels.add(item.jobLabel)
      summaries.set(item.dateKey, existing)
    })

    return new Map(
      Array.from(summaries.entries()).map(([dateKey, summary]) => [
        dateKey,
        {
          entryCount: summary.recordCount,
          workerCount: 0,
          jobCount: summary.jobLabels.size,
          hasAttention: false,
          resourceQuantity: summary.quantityTotal,
          resourceTitles: summary.titleLabels.size,
        },
      ])
    )
  }, [resourceItems])

  const sortedSelectedDateResources = useMemo(() => {
    return [...selectedDateResources].sort((left, right) => {
      if (Boolean(left.to_be_purchased) !== Boolean(right.to_be_purchased)) {
        return left.to_be_purchased ? -1 : 1
      }

      return left.titleLabel.localeCompare(right.titleLabel)
    })
  }, [selectedDateResources])

  useEffect(() => {
    setSelectedWorkDayNotes(selectedWorkDay?.notes ?? '')
    setSelectedWorkDayPlanned(Boolean(selectedWorkDay?.is_planned))
    setSelectedWorkDayWorked(Boolean(selectedWorkDay?.is_worked))
  }, [selectedWorkDay])

  const createWorkDayMutation = useMutation({
    mutationFn: () =>
      assignmentsApi.createWorkDay({
        job: selectedJobId,
        work_date: selectedDateIso,
      }),
    onSuccess: () => {
      toast.success(`${labels.workDay} created successfully`)
      queryClient.invalidateQueries({ queryKey: ['workdays'] })
      queryClient.invalidateQueries({ queryKey: ['workdays', 'calendar', selectedJobId] })
      queryClient.invalidateQueries({ queryKey: ['workday-entries', 'selected-date'] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const updateWorkDayMutation = useMutation({
    mutationFn: () =>
      assignmentsApi.updateWorkDay(String(selectedWorkDay!.id), {
        job: selectedJobId,
        work_date: selectedWorkDay!.work_date,
        is_planned: selectedWorkDayPlanned,
        is_worked: selectedWorkDayWorked,
        notes: selectedWorkDayNotes.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success(`${labels.workDay} updated successfully`)
      queryClient.invalidateQueries({ queryKey: ['workdays'] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const deleteWorkDayMutation = useMutation({
    mutationFn: () => assignmentsApi.deleteWorkDay(String(selectedWorkDay!.id)),
    onSuccess: () => {
      toast.success(`${labels.workDay} deleted successfully`)
      setIsDeleteWorkDayOpen(false)
      queryClient.invalidateQueries({ queryKey: ['workdays', 'calendar', selectedJobId] })
      queryClient.invalidateQueries({ queryKey: ['workday-entries', 'selected-date'] })
      queryClient.invalidateQueries({ queryKey: ['workday-entries', 'attention'] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const markParticularPurchasedMutation = useMutation({
    mutationFn: (particularId: string) =>
      financeApi.updateParticular(particularId, {
        to_be_purchased: false,
      }),
    onSuccess: () => {
      toast.success('Marked as purchased')
      queryClient.invalidateQueries({ queryKey: ['particulars'] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const deleteParticularMutation = useMutation({
    mutationFn: financeApi.deleteParticular,
    onSuccess: () => {
      toast.success('Particular deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['particulars'] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const deleteWorkDayEntryMutation = useMutation({
    mutationFn: assignmentsApi.deleteWorkDayEntry,
    onSuccess: () => {
      toast.success(`${labels.workDayEntry} deleted successfully`)
      queryClient.invalidateQueries({ queryKey: ['workday-entries'] })
      queryClient.invalidateQueries({ queryKey: ['workday-entries', 'selected-date'] })
      queryClient.invalidateQueries({ queryKey: ['workday-entries', 'attention'] })
      queryClient.invalidateQueries({ queryKey: ['workdays'] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const addWorkerEntryMutation = useMutation({
    mutationFn: () => {
      if (!selectedWorkDay) {
        throw new Error(`Create a ${labels.workDayLower} before adding a ${workerEntitySingularLower}.`)
      }

      return assignmentsApi.syncWorkDayAssignmentEntries(String(selectedWorkDay.id))
    },
    onSuccess: (result) => {
      const createdCount = Number(result.created_entries ?? 0)
      toast.success(
        createdCount === 1
          ? `Added 1 ${workerEntitySingularLower} entry`
          : createdCount > 1
            ? `Added ${createdCount} ${workerEntitySingularLower} entries`
            : `No missing assignment ${workerEntityPluralLower} to add`
      )
      queryClient.invalidateQueries({ queryKey: ['workday-entries'] })
      queryClient.invalidateQueries({ queryKey: ['workday-entries', 'selected-date'] })
      queryClient.invalidateQueries({ queryKey: ['workday-entries', 'attention'] })
      queryClient.invalidateQueries({ queryKey: ['workdays'] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const setWorkDayEntryStatusMutation = useMutation({
    mutationFn: ({
      id,
      status,
      checkIn,
      checkOut,
      reason,
    }: {
      id: string
      status: string
      checkIn?: string
      checkOut?: string
      reason?: string
    }) =>
      assignmentsApi.setWorkDayEntryStatus(id, status, {
        checkIn,
        checkOut,
        reason,
      }),
    onMutate: ({ id, status }) => {
      queryClient.setQueryData(
        [
          'workday-entries',
          'selected-date',
          selectedJobId,
          selectedDateIso,
          isAllJobsSelected ? 'all-jobs' : String(selectedWorkDayForQuery?.id ?? ''),
          selectedWorkerId,
          showAttentionOnly,
        ],
        (current: unknown) => patchWorkDayEntryStatusInCache(current, id, status),
      )
      queryClient.setQueryData(['workday-entries'], (current: unknown) =>
        patchWorkDayEntryStatusInCache(current, id, status),
      )
    },
    onSuccess: (_data, variables) => {
      toast.success(`${labels.workDayEntry} status updated`)
      queryClient.invalidateQueries({ queryKey: ['workday-entries'] })
      queryClient.invalidateQueries({ queryKey: ['workday-entries', 'selected-date'] })
      queryClient.invalidateQueries({ queryKey: ['workday-entries', 'attention'] })
      queryClient.invalidateQueries({ queryKey: ['workday-entry', String(variables.id)] })
      queryClient.invalidateQueries({ queryKey: ['job-workflow-transitions', 'workday_entry', String(variables.id)] })
      queryClient.invalidateQueries({ queryKey: ['job-cancellation-reasons', 'workday_entry', String(variables.id)] })
      queryClient.invalidateQueries({ queryKey: ['workdays'] })
      queryClient.invalidateQueries({ queryKey: ['jobs'] })
      queryClient.invalidateQueries({ queryKey: ['workday-entry-audits'] })
      queryClient.invalidateQueries({ queryKey: ['job-work-day-entries'] })
      queryClient.invalidateQueries({ queryKey: ['worker-work-day-entries'] })
      queryClient.invalidateQueries({ queryKey: ['assignment-workday-entries'] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const calendarMarkedDates = useMemo(() => {
    if (calendarMode === 'resources') {
      return Array.from(resourceSummaryByDate.keys()).map((dateKey) => new Date(`${dateKey}T00:00:00`))
    }

    return workDays.map((workDay) => new Date(workDay.work_date))
  }, [calendarMode, resourceSummaryByDate, workDays])

  const isJobScopedView = !isAllJobsSelected
  const pageTitle = isJobScopedView
    ? formatJobTitleWithId({ jobId: selectedJobId, title: selectedJob?.title, jobRef: selectedJob?.job_ref, entityLabel: labels.entity })
    : labels.workDayPlural
  const backPath = resolveReturnToPath(
    location.search,
    withServiceInstanceId(`${labels.basePath}/${selectedJobId}`, serviceInstanceId),
  )
  const selectedDateHeading = formatWorkDayDateWithWeekday(selectedDateIso, organizationDateLocale)
  const canAddInventoryParticular = Boolean(!isAllJobsSelected && selectedJobId && !jobScheduleBlockedMessage)
  const isParticularMutationDisabled = Boolean(jobScheduleBlockedMessage)

  const editingParticularJobId = useMemo(() => {
    if (!editingParticular) {
      return selectedJobId
    }

    const rawJobId = editingParticular.job_id
      ?? (typeof editingParticular.job === 'object' ? editingParticular.job?.id : editingParticular.job)

    return rawJobId ? String(rawJobId) : selectedJobId
  }, [editingParticular, selectedJobId])

  const editingParticularJobTitle = useMemo(() => {
    if (!editingParticularJobId || editingParticularJobId === 'all') {
      return selectedJob?.title
    }

    return jobTitleById.get(editingParticularJobId) || selectedJob?.title
  }, [editingParticularJobId, jobTitleById, selectedJob?.title])

  const handleEditParticularOpenChange = (open: boolean) => {
    setIsEditParticularOpen(open)
    if (!open) {
      setEditingParticular(null)
    }
  }

  if (selectedJobQuery.error || workDaysQuery.error || selectedDateEntriesQuery.error || attentionEntriesQuery.error || assignmentsQuery.error || particularsQuery.error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-destructive mb-2">{labels.workDayCalendarError}</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <WorkerLifecycleNav
        activeStage="workday-entries"
        basePath={labels.basePath as '/job' | '/projects' | '/events'}
        workdayEntriesLabel={labels.workDayEntryPlural}
        description={`Move from ${workerEntitySingularLower} onboarding to skills, pay rates, assignments, and daily ${labels.workDayEntryPluralLower}.`}
      />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-3">
          {requestedJobId && isJobScopedView ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(backPath)}
              title="Back"
              className="mt-0.5 h-10 w-10 shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          ) : null}
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold text-foreground">{pageTitle}</h1>
              {!isJobScopedView ? (
                <button
                  type="button"
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition hover:bg-muted hover:text-foreground"
                  title={labels.workDaysBrowseDescription}
                  aria-label="Work days information"
                >
                  <Info className="h-4 w-4" />
                </button>
              ) : null}
              {isJobScopedView ? (
                <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">
                  {labels.scheduleBadge}
                </Badge>
              ) : null}
            </div>
            {isJobScopedView ? (
              <p className="mt-1 text-sm text-muted-foreground">{labels.scheduleScopedDescription}</p>
            ) : null}
          </div>
        </div>

        {calendarMode === 'workers' ? (
          <div className="flex flex-wrap gap-2 text-sm">
            <Badge variant="outline" className="border-border/70 bg-background/80">
              {visibleMonthWorkDays.length} {labels.workDayPluralLower} this month
            </Badge>
            <Badge variant="outline" className="border-border/70 bg-background/80">
              {selectedDateEntries.length} entries on {formatDate(selectedDate, organizationDateLocale)}
            </Badge>
            <button
              type="button"
              onClick={() => setShowAttentionOnly((current) => !current)}
              className={cn(
                'inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition',
                showAttentionOnly
                  ? 'border-amber-300 bg-amber-50 text-amber-800'
                  : 'border-border/70 bg-background/80 text-muted-foreground hover:border-amber-200 hover:text-amber-800',
              )}
            >
              {attentionNeededJobsCount} need attention
            </button>
          </div>
        ) : null}
      </div>

      {isJobScopedView && jobScheduleBlockedMessage ? (
        <Card className="border-amber-300 bg-amber-50/90 shadow-sm">
          <CardContent className="p-5 sm:p-6">
            <p className="text-sm text-amber-950 sm:text-base">{jobScheduleBlockedMessage}</p>
          </CardContent>
        </Card>
      ) : null}

      <div className="rounded-[28px] border border-border/70 bg-card/80 p-4 shadow-sm">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px_220px_auto] lg:items-end">
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">{labels.entity}</p>
            <SearchableSelect
              ariaLabel={labels.entity}
              value={selectedJobId}
              onValueChange={handleJobChange}
              onSearchChange={setJobSearch}
              options={jobOptions}
              placeholder={`All ${labels.entityPluralLower}`}
              searchPlaceholder={`Type ${labels.entityLower} title, ref, or ID`}
              emptyText={jobSearchQuery.isFetching ? `Searching ${labels.entityPluralLower}...` : `No ${labels.entityPluralLower} found.`}
              emptySearchText={`Type at least 2 characters to search ${labels.entityPluralLower}.`}
              filterLocally={false}
              showAllOnEmptySearch
              disabled={isJobFilterLocked}
            />
            {isJobFilterLocked ? (
              <p className="text-xs text-muted-foreground">
                {labels.entity} is fixed from the link. Go back to the {labels.entityLower} page to change context.
              </p>
            ) : null}
            {isJobScopedView && selectedJob?.status ? (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Badge
                  variant="outline"
                  className={cn('h-5 px-1.5 text-[10px] font-medium', getJobStatusBadgeClassName(selectedJob.status))}
                >
                  {getJobStatusLabel(selectedJob.status)}
                </Badge>
              </div>
            ) : null}
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">View</p>
            <Tabs value={calendarMode} onValueChange={(value) => setCalendarMode(value as CalendarViewMode)}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="workers">{workerEntityPlural}</TabsTrigger>
                <TabsTrigger value="resources">Resources</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {showTopWorkerFilter ? (
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">{workerEntitySingular}</p>
              <SearchableSelect
                ariaLabel={`Filter by ${workerEntitySingularLower}`}
                value={selectedWorkerId}
                onValueChange={setSelectedWorkerId}
                options={workerFilterOptions}
                placeholder={`All ${workerEntityPluralLower}`}
                searchPlaceholder={`Search ${workerEntityPluralLower}`}
                emptyText={`No ${workerEntityPluralLower} found.`}
                disabled={assignmentsQuery.isLoading}
              />
            </div>
          ) : calendarMode === 'resources' ? (
            <div className="rounded-xl border border-border/60 bg-muted/20 px-4 py-3 text-sm text-muted-foreground lg:col-span-2">
              Resources mode uses particular purchase dates.
            </div>
          ) : null}

          {calendarMode === 'workers' && showAttentionOnly ? (
            <Badge variant="outline" className="justify-self-start border-amber-200 bg-amber-50 text-amber-800 lg:justify-self-end">
              Attention filter on
            </Badge>
          ) : null}
        </div>
      </div>

      <div
        className={cn(
          'grid gap-5',
          isFullCalendarView
            ? 'grid-cols-1'
            : 'xl:grid-cols-[minmax(360px,420px)_minmax(0,1fr)] xl:items-start',
        )}
      >
        <Card
          className={cn(
            'overflow-hidden rounded-[28px] border-border/70 bg-card/90 shadow-lg',
            !isFullCalendarView && 'xl:sticky xl:top-4',
          )}
        >
          <CardHeader className="border-b border-border/60 bg-muted/20 pb-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <CalendarDays className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-lg font-semibold">Calendar</CardTitle>
                  <CardDescription>
                    {isFullCalendarView
                      ? 'Full month view — pick a date, then manage entries below.'
                      : 'Select a date to view and manage entries.'}
                  </CardDescription>
                </div>
              </div>
              <div className="flex shrink-0 rounded-xl border border-border/70 bg-background p-1">
                <Button
                  type="button"
                  variant={isFullCalendarView ? 'ghost' : 'secondary'}
                  size="sm"
                  className="h-8 rounded-lg px-2.5 text-xs"
                  onClick={() => setPageLayout('split')}
                  aria-pressed={!isFullCalendarView}
                >
                  <LayoutPanelLeft className="mr-1.5 h-3.5 w-3.5" />
                  Split
                </Button>
                <Button
                  type="button"
                  variant={isFullCalendarView ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-8 rounded-lg px-2.5 text-xs"
                  onClick={() => setPageLayout('full')}
                  aria-pressed={isFullCalendarView}
                >
                  <Maximize2 className="mr-1.5 h-3.5 w-3.5" />
                  Full calendar
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-4 p-5">
            {workDaysQuery.isLoading || (calendarMode === 'resources' && particularsQuery.isLoading) ? (
              <div className="space-y-3">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-[360px] w-full" />
              </div>
            ) : (
              <>
                <div
                  className={cn(
                    'min-w-0 overflow-hidden rounded-2xl border border-border/70 bg-background p-2',
                    isFullCalendarView && 'mx-auto max-w-6xl p-3 sm:p-4',
                  )}
                >
                  <Calendar
                    mode="single"
                    month={visibleMonth}
                    onMonthChange={setVisibleMonth}
                    selected={selectedDate}
                    onSelect={(date) => {
                      if (date) {
                        setSelectedDate(date)
                      }
                    }}
                    modifiers={{
                      hasWorkday: calendarMarkedDates,
                    }}
                    modifiersClassNames={{
                      hasWorkday: 'bg-emerald-50 text-emerald-900 font-medium rounded-md',
                    }}
                    components={{
                      DayButton: (props) => (
                        <WorkDayCalendarButton
                          {...props}
                          calendarMode={calendarMode}
                          calendarSummaryByDate={calendarMode === 'workers' ? calendarSummaryByDate : resourceSummaryByDate}
                          showWorkerAndJobCounts={calendarMode === 'workers' && selectedWorkerId === 'all'}
                          attentionDates={attentionDates}
                          workDayByDate={workDayMap}
                        />
                      ),
                    }}
                    className={cn(
                      'mx-auto w-full max-w-full',
                      isFullCalendarView
                        ? '[--cell-size:2.85rem] sm:[--cell-size:3.25rem] md:[--cell-size:3.75rem] lg:[--cell-size:4.25rem] xl:[--cell-size:4.5rem]'
                        : '[--cell-size:2.35rem] sm:[--cell-size:2.5rem] md:[--cell-size:2.6rem]',
                    )}
                  />
                </div>

                <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground">
                  {calendarMode === 'workers' ? (
                    <>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
                        {labels.workDay} exists
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-amber-500 text-[9px] font-bold leading-none text-white">
                          !
                        </span>
                        Needs review
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="rounded-full bg-sky-700 px-1.5 py-0.5 text-[10px] font-semibold text-white">n</span>
                        Entry count
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="inline-flex h-2.5 w-2.5 rounded-full bg-sky-500" />
                        Resource date
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="rounded-full bg-slate-900 px-1.5 py-0.5 text-[10px] font-semibold text-white">R</span>
                        Quantity
                      </span>
                    </>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card className="overflow-hidden rounded-[28px] border-border/70 bg-card/90 shadow-lg">
            <CardHeader className="border-b border-border/60 bg-muted/20 pb-4">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <CardTitle className="text-xl font-semibold">{selectedDateHeading}</CardTitle>
                  <CardDescription className="mt-1">
                    {calendarMode === 'workers'
                      ? selectedDateEntries.length > 0
                        ? `${selectedDateEntries.length} ${workerEntitySingularLower} entr${selectedDateEntries.length === 1 ? 'y' : 'ies'}${selectedWorkerId === 'all' ? '' : ` for ${selectedWorkerLabel}`}.`
                        : selectedWorkDay
                          ? `No entries yet. Add a ${workerEntitySingularLower} slot below.`
                          : createBlockedReason || `Create a ${labels.workDayLower} for this date to start adding entries.`
                      : selectedDateResources.length > 0
                        ? `${selectedDateResources.length} inventory particular${selectedDateResources.length === 1 ? '' : 's'} scheduled.`
                        : isAllJobsSelected
                          ? 'Select a job to view or add inventory particulars for this date.'
                          : 'No inventory particulars for this date.'}
                  </CardDescription>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {calendarMode === 'workers' && !isAllJobsSelected && selectedWorkDay ? (
                    <>
                      <Badge variant="outline" className={selectedWorkDay.is_planned ? 'bg-sky-50 text-sky-700 border-sky-200' : 'bg-slate-50 text-slate-700 border-slate-200'}>
                        {selectedWorkDay.is_planned ? 'Planned' : 'Unplanned'}
                      </Badge>
                      <Badge variant="outline" className={selectedWorkDay.is_worked ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>
                        {selectedWorkDay.is_worked ? 'Worked' : 'Open'}
                      </Badge>
                      <Button
                        onClick={() => addWorkerEntryMutation.mutate()}
                        disabled={addWorkerEntryMutation.isPending || isWorkDayMutationDisabled}
                        title={
                          addableWorkerAssignments.length === 0
                            ? `Sync ${workerEntitySingularLower} entries from ${labels.entityLower} assignments. Use Add ${workerEntitySingularLower} entry if no assignments are available.`
                            : `Add ${addableWorkerAssignments.length} ${workerEntitySingularLower} entr${addableWorkerAssignments.length === 1 ? 'y' : 'ies'} from assignments`
                        }
                      >
                        {addWorkerEntryMutation.isPending ? <Spinner className="mr-2" /> : <Plus className="mr-2 h-4 w-4" />}
                        {`Add ${workerEntitySingularLower}`}
                      </Button>
                      {addableWorkerAssignments.length === 0 && manualWorkDayEntryPath ? (
                        <Button variant="outline" asChild>
                          <Link to={manualWorkDayEntryPath}>
                            <Plus className="mr-2 h-4 w-4" />
                            {`Add ${workerEntitySingularLower} entry`}
                          </Link>
                        </Button>
                      ) : null}
                      <Button
                        variant="outline"
                        className="border-destructive/20 text-destructive hover:bg-destructive/10"
                        onClick={() => setIsDeleteWorkDayOpen(true)}
                        disabled={
                          deleteWorkDayMutation.isPending
                          || isWorkDayMutationDisabled
                          || !canDeleteSelectedWorkDay
                        }
                      >
                        {deleteWorkDayMutation.isPending ? <Spinner className="mr-2" /> : <Trash2 className="mr-2 h-4 w-4" />}
                        {`Delete ${labels.workDayLower}`}
                      </Button>
                    </>
                  ) : null}
                  {calendarMode === 'workers' && !isAllJobsSelected && !selectedWorkDay ? (
                    <Button
                      onClick={() => createWorkDayMutation.mutate()}
                      disabled={createWorkDayMutation.isPending || Boolean(createBlockedReason)}
                    >
                      {createWorkDayMutation.isPending ? <Spinner className="mr-2" /> : <Plus className="mr-2 h-4 w-4" />}
                      {`Create ${labels.workDayLower}`}
                    </Button>
                  ) : null}
                  {calendarMode === 'resources' ? (
                    <>
                      {canAddInventoryParticular ? (
                        <AddInventoryParticularDialog
                          jobId={selectedJobId}
                          jobTitle={selectedJob?.title}
                          workDate={selectedDateIso}
                          purchaseBy={selectedDateIso}
                          purchaseByLocale={organizationDateLocale}
                          serviceInstanceId={serviceInstanceId}
                          triggerLabel="Add inventory"
                        />
                      ) : null}
                      <Button variant="outline" size="sm" onClick={() => setCalendarMode('workers')}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {`${workerEntityPlural} view`}
                      </Button>
                    </>
                  ) : null}
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4 p-5">
              {selectedJobQuery.isLoading || workDaysQuery.isLoading || assignmentsQuery.isLoading || (calendarMode === 'resources' && particularsQuery.isLoading) ? (
                <div className="space-y-3">
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-40 w-full" />
                </div>
              ) : (
                <>
                  {calendarMode === 'workers' && createBlockedReason && !selectedWorkDay && !isAllJobsSelected && !jobScheduleBlockedMessage ? (
                    <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-sm text-amber-900">
                      {createBlockedReason}
                    </div>
                  ) : null}

                  {calendarMode === 'workers' && !isAllJobsSelected && selectedWorkDay ? (
                    <div className="rounded-2xl border border-border/70 bg-background/80">
                      <button
                        type="button"
                        onClick={() => setShowWorkDayDetails((current) => !current)}
                        className="flex w-full items-center justify-between px-4 py-3 text-left"
                      >
                        <div>
                          <p className="text-sm font-medium text-foreground">{`${labels.workDay} settings`}</p>
                          <p className="text-xs text-muted-foreground">Planned/worked flags and day notes</p>
                        </div>
                        {showWorkDayDetails ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                      </button>

                      {showWorkDayDetails ? (
                        <fieldset disabled={isWorkDayMutationDisabled} className="space-y-4 border-t border-border/60 px-4 pb-4 pt-4 disabled:cursor-not-allowed disabled:opacity-60">
                          <div className="grid gap-4 sm:grid-cols-2">
                            <div className="flex items-center justify-between rounded-xl border border-border/60 bg-muted/20 px-4 py-3">
                              <div>
                                <p className="text-sm font-medium text-foreground">Planned day</p>
                                <p className="text-xs text-muted-foreground">Part of the intended schedule</p>
                              </div>
                              <Switch checked={selectedWorkDayPlanned} onCheckedChange={setSelectedWorkDayPlanned} />
                            </div>
                            <div className="flex items-center justify-between rounded-xl border border-border/60 bg-muted/20 px-4 py-3">
                              <div>
                                <p className="text-sm font-medium text-foreground">Worked day</p>
                                <p className="text-xs text-muted-foreground">Actual execution on this date</p>
                              </div>
                              <Switch checked={selectedWorkDayWorked} onCheckedChange={setSelectedWorkDayWorked} />
                            </div>
                          </div>
                          <Textarea
                            rows={3}
                            placeholder={`Add notes for the selected ${labels.workDayLower}`}
                            value={selectedWorkDayNotes}
                            onChange={(event) => setSelectedWorkDayNotes(event.target.value)}
                          />
                          <div className="flex flex-wrap justify-end gap-2">
                            {!canDeleteSelectedWorkDay ? (
                              <p className="w-full text-xs text-muted-foreground">
                                {`${labels.workDayPlural} can be deleted only while entries are pending acceptance, scheduled, declined, or draft with no attendance logged.`}
                              </p>
                            ) : null}
                            <Button onClick={() => updateWorkDayMutation.mutate()} disabled={updateWorkDayMutation.isPending || isWorkDayMutationDisabled}>
                              {updateWorkDayMutation.isPending ? <Spinner className="mr-2" /> : null}
                              {`Save ${labels.workDayLower}`}
                            </Button>
                          </div>
                        </fieldset>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="space-y-2">
                    {calendarMode === 'workers' && selectedDateEntries.length > 0 ? (
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Entries · {selectedDateEntries.length}
                      </p>
                    ) : null}

                    {calendarMode === 'workers' ? (
                      selectedDateEntriesQuery.isLoading || selectedDateEntriesQuery.isFetching ? (
                        <div className="space-y-3">
                          <Skeleton className="h-20 w-full" />
                          <Skeleton className="h-20 w-full" />
                        </div>
                      ) : selectedDateEntries.length > 0 ? (
                        <div className="space-y-1.5">
                          {selectedDateEntries.map((entry) => {
                            const entryAssignment = assignmentByWorkerKey.get(`${entry.job}:${entry.worker}`)
                            const entryJob = jobById.get(String(entry.job))
                            const entryJobBlockedMessage = getWorkdayEntryJobBlockedMessage(entryJob)
                            const entryActionsBlocked = Boolean(jobScheduleBlockedMessage || entryJobBlockedMessage)
                            const entryAssignmentBlocked = isTerminalAssignmentStatus(entryAssignment?.status)
                            const isDeletingEntry = deleteWorkDayEntryMutation.isPending
                              && deleteWorkDayEntryMutation.variables === String(entry.id)

                            return (
                              <WorkDayEntryCard
                                key={entry.id}
                                entry={entry}
                                showJob={isAllJobsSelected}
                                serviceInstanceId={serviceInstanceId}
                                returnPath={currentReturnPath}
                                organizationLocale={organizationDateLocale}
                                organizationTimeZone={organizationTimeZone}
                                blocked={entryActionsBlocked}
                                assignmentBlocked={entryAssignmentBlocked}
                                assignmentStatus={entryAssignment?.status}
                                isDeleting={isDeletingEntry}
                                onDelete={() => deleteWorkDayEntryMutation.mutate(String(entry.id))}
                                allowGroupWorkdayAccess={canGroupWorkdayStatusOverride}
                                onStatusChange={(status, options) => {
                                  const entryStatus = resolveWorkDayEntryStatusFromEntry(entry)
                                  if (status !== entryStatus) {
                                    if (status === 'completed') {
                                      setPendingEntryCompletion({
                                        id: String(entry.id),
                                        status,
                                        checkIn: options?.checkIn,
                                        checkOut: options?.checkOut,
                                        reason: options?.reason,
                                        jobId: String(entry.job),
                                        workDayId: entry.work_day,
                                      })
                                      return
                                    }
                                    setWorkDayEntryStatusMutation.mutate({
                                      id: String(entry.id),
                                      status,
                                      checkIn: options?.checkIn,
                                      checkOut: options?.checkOut,
                                      reason: options?.reason,
                                    })
                                  }
                                }}
                              />
                            )
                          })}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border/70 bg-muted/10 px-6 py-12 text-center text-muted-foreground">
                          <CalendarIcon className="h-8 w-8" />
                          <div>
                            <p className="font-medium text-foreground">
                              {selectedWorkDay ? 'No entries on this work day yet' : 'No work day for this date'}
                            </p>
                            <p className="mt-1 text-sm text-muted-foreground">
                              {selectedWorkDay
                                ? addableWorkerAssignments.length > 0
                                  ? `Use Add ${workerEntitySingularLower} above to sync entries from ${labels.entityLower} assignments.`
                                  : manualWorkDayEntryPath
                                    ? `Use Add ${workerEntitySingularLower} to sync from assignments, or Add ${workerEntitySingularLower} entry to create one manually.`
                                    : `Use Add ${workerEntitySingularLower} above to sync entries from ${labels.entityLower} assignments.`
                                : `Create a ${labels.workDayLower} first, then add ${workerEntitySingularLower} entries.`}
                            </p>
                          </div>
                        </div>
                      )
                    ) : (
                      <div className="rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Particular</TableHead>
                              <TableHead>Quantity</TableHead>
                              <TableHead>Cost</TableHead>
                              <TableHead>In charge</TableHead>
                              <TableHead>Purchase</TableHead>
                              {isAllJobsSelected ? <TableHead>Job</TableHead> : null}
                              <TableHead>Notes</TableHead>
                              <TableHead className="w-[4rem] text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {sortedSelectedDateResources.length > 0 ? (
                              sortedSelectedDateResources.map((item) => {
                                const normalizedNote = truncateText(item.notes, 96)
                                const isMarkingPurchased = markParticularPurchasedMutation.isPending
                                  && markParticularPurchasedMutation.variables === String(item.id)

                                return (
                                  <TableRow key={item.id}>
                                    <TableCell className="font-medium text-foreground">{item.titleLabel}</TableCell>
                                    <TableCell>{formatNumber(item.quantityValue)}</TableCell>
                                    <TableCell>
                                      <div className="space-y-1">
                                        <div className="font-medium text-foreground">{formatMoney(item.cost ?? item.unit_price)}</div>
                                        <Badge
                                          variant="outline"
                                          className={item.payable_by_client
                                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                            : 'border-slate-200 bg-slate-50 text-slate-600'}
                                        >
                                          {item.payable_by_client ? 'Client payable' : 'Internal only'}
                                        </Badge>
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      {item.responsible_worker_name ? (
                                        <span className="text-sm text-foreground">{item.responsible_worker_name}</span>
                                      ) : (
                                        <span className="text-sm text-muted-foreground">Unassigned</span>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {item.to_be_purchased ? (
                                        <div className="space-y-1">
                                          <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
                                            <ShoppingCart className="mr-1.5 h-3.5 w-3.5" />
                                            Purchase needed
                                          </Badge>
                                          <div className="text-xs text-muted-foreground">
                                            By {formatDate(item.purchase_by, organizationDateLocale)}
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="space-y-1">
                                          <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                                            <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                                            Purchased
                                          </Badge>
                                          {item.purchase_by ? (
                                            <div className="text-xs text-muted-foreground">
                                              By {formatDate(item.purchase_by, organizationDateLocale)}
                                            </div>
                                          ) : null}
                                        </div>
                                      )}
                                    </TableCell>
                                    {isAllJobsSelected ? (
                                      <TableCell>
                                        <div className="max-w-[18rem] text-sm text-muted-foreground">
                                          {item.jobId ? (
                                            <JobTitleLink
                                              jobId={item.jobId}
                                              title={item.jobLabel}
                                              serviceInstanceId={serviceInstanceId}
                                              returnPath={currentReturnPath}
                                              className="font-medium"
                                            />
                                          ) : (
                                            item.jobLabel
                                          )}
                                        </div>
                                      </TableCell>
                                    ) : null}
                                    <TableCell>
                                      {normalizedNote ? (
                                        <p className="max-w-[18rem] text-xs text-muted-foreground">{normalizedNote}</p>
                                      ) : (
                                        <span className="text-sm text-muted-foreground">No notes</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-9 w-9 rounded-lg"
                                          >
                                            <MoreHorizontal className="h-4 w-4" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-48 rounded-lg">
                                          <DropdownMenuItem
                                            disabled={isParticularMutationDisabled}
                                            onClick={() => {
                                              setEditingParticular(item)
                                              setIsEditParticularOpen(true)
                                            }}
                                          >
                                            <Pencil className="mr-2 h-4 w-4" />
                                            Edit
                                          </DropdownMenuItem>
                                          {item.to_be_purchased ? (
                                            <DropdownMenuItem
                                              disabled={isParticularMutationDisabled || markParticularPurchasedMutation.isPending}
                                              onClick={() => markParticularPurchasedMutation.mutate(String(item.id))}
                                            >
                                              {isMarkingPurchased ? <Spinner className="mr-2 h-4 w-4" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                                              Mark as purchased
                                            </DropdownMenuItem>
                                          ) : null}
                                          <DropdownMenuItem
                                            className="text-destructive"
                                            disabled={isParticularMutationDisabled || deleteParticularMutation.isPending}
                                            onClick={() => deleteParticularMutation.mutate(String(item.id))}
                                          >
                                            <Trash2 className="mr-2 h-4 w-4" />
                                            Delete
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </TableCell>
                                  </TableRow>
                                )
                              })
                            ) : (
                              <TableRow>
                                <TableCell colSpan={isAllJobsSelected ? 8 : 7} className="h-32 text-center">
                                  <div className="flex flex-col items-center justify-center gap-3 text-muted-foreground">
                                    {canAddInventoryParticular ? (
                                      <AddInventoryParticularDialog
                                        jobId={selectedJobId}
                                        jobTitle={selectedJob?.title}
                                        workDate={selectedDateIso}
                                        purchaseBy={selectedDateIso}
                                        purchaseByLocale={organizationDateLocale}
                                        serviceInstanceId={serviceInstanceId}
                                        triggerLabel="Add inventory particular"
                                      />
                                    ) : null}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {editingParticular ? (
        <AddInventoryParticularDialog
          hideTrigger
          open={isEditParticularOpen}
          onOpenChange={handleEditParticularOpenChange}
          particular={editingParticular}
          jobId={editingParticularJobId}
          jobTitle={editingParticularJobTitle}
          workDate={selectedDateIso}
          purchaseBy={editingParticular.purchase_by?.slice(0, 10) || selectedDateIso}
                        purchaseByLocale={organizationDateLocale}
                        serviceInstanceId={serviceInstanceId}
                      />
      ) : null}

      <AlertDialog open={isDeleteWorkDayOpen} onOpenChange={setIsDeleteWorkDayOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete work day</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedDateEntries.length > 0
                ? `This work day has ${selectedDateEntries.length} entr${selectedDateEntries.length === 1 ? 'y' : 'ies'}. Delete all entries before removing the work day.`
                : 'This removes the selected parent work day from the calendar.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteWorkDayMutation.mutate()}
              disabled={!canDeleteSelectedWorkDay}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteWorkDayMutation.isPending ? 'Deleting...' : 'Delete Work Day'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {pendingEntryCompletion ? (
        <ChecklistCompletionDialog
          open={Boolean(pendingEntryCompletion)}
          onOpenChange={(open) => {
            if (!open) {
              setPendingEntryCompletion(null)
            }
          }}
          jobId={pendingEntryCompletion.jobId}
          workDayId={pendingEntryCompletion.workDayId}
          kind={_portalMode}
          title="Complete daily checklist"
          description="Confirm all required daily checklist steps are done before marking this work day entry as completed."
          confirming={setWorkDayEntryStatusMutation.isPending}
          onConfirmed={() => {
            setWorkDayEntryStatusMutation.mutate(
              {
                id: pendingEntryCompletion.id,
                status: pendingEntryCompletion.status,
                checkIn: pendingEntryCompletion.checkIn,
                checkOut: pendingEntryCompletion.checkOut,
                reason: pendingEntryCompletion.reason,
              },
              {
                onSuccess: () => {
                  setPendingEntryCompletion(null)
                },
              },
            )
          }}
        />
      ) : null}
    </div>
  )
}
